import React, { useState } from "react";
import ConfirmModal from "../ConfirmModal";
import { printingApi } from "../../lib/printing/printingApi";
import { agentIsOnline, formatWhen, operatorMessage } from "../../lib/printing/printingModel";
import { SectionCard, Field, inputCls, btnPrimary, btnSecondary, btnDanger, Modal, Pill, EmptyState, Notice } from "./PrintingUi";

/**
 * Print agents: the tablet (Android) or PC (Windows) programs that poll
 * for jobs. A token is shown exactly once, at issuance or rotation, in a
 * dialog that keeps it in React state only — never in a URL, log,
 * storage or toast. Existing tokens are never displayed; the list shows
 * the 8-character prefix the backend stores for identification.
 */
export default function PrintAgentsPanel({ agents, printers, isAdmin, onChanged, notify }) {
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("android");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [revealed, setRevealed] = useState(null);        // { agent, token, rotated }
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(null);          // { kind: "rotate"|"revoke", agent }

  const printerCount = (agentId) => printers.filter((p) => p.agent_id === agentId && !p.deleted_at).length;

  const issue = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setFormError("Give the agent a name, for example Kitchen tablet."); return; }
    setBusy(true); setFormError(null);
    try {
      const data = await printingApi.issueAgent({ name: name.trim(), platform });
      setRevealed({ agent: data.agent, token: data.token, rotated: false });
      setName("");
      onChanged();
    } catch (err) { setFormError(operatorMessage(err)); }
    finally { setBusy(false); }
  };

  const runConfirmed = async () => {
    const { kind, agent } = confirm;
    setBusy(true);
    try {
      if (kind === "rotate") {
        const data = await printingApi.rotateAgent(agent.id);
        setRevealed({ agent: data.agent || agent, token: data.token, rotated: true });
      } else {
        await printingApi.revokeAgent(agent.id);
        notify("success", `Token revoked for ${agent.name}. That agent will stop polling on its next request.`);
      }
      setConfirm(null);
      onChanged();
    } catch (err) { notify("error", operatorMessage(err)); }
    finally { setBusy(false); }
  };

  const copyToken = async () => {
    try { await navigator.clipboard.writeText(revealed.token); setCopied(true); } catch (_e) { setCopied(false); notify("error", "Copy failed. Select the token and copy it manually."); }
  };

  const closeReveal = () => { setRevealed(null); setCopied(false); };

  return (
    <div className="space-y-4" data-testid="agents-panel">
      <SectionCard title="Print agents" description="A print agent is the app on your tablet (Android) or PC (Windows) that receives print jobs. Each agent has its own token.">
        {agents.length === 0 ? (
          <EmptyState testId="agents-empty">No print agents yet. Issue a token below, then paste it into the Rudras Print Agent app on the tablet.</EmptyState>
        ) : (
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="agents-list">
            {agents.map((a) => {
              const online = agentIsOnline(a);
              const revoked = Boolean(a.revoked_at);
              return (
                <li key={a.id} className="rounded-md border border-stone-200 p-3 space-y-2" data-testid={`agent-card-${a.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-semibold text-stone-900">{a.name}</div>
                      <div className="text-xs text-stone-600">{(a.platform || "windows") === "android" ? "Android tablet" : "Windows PC"} · token starts with <span className="font-mono">{a.token_prefix || "????????"}…</span></div>
                    </div>
                    <Pill tone={revoked ? "neutral" : online ? "success" : "warning"} testId={`agent-status-${a.id}`}>
                      {revoked ? "Revoked" : online ? "Online" : "Offline"}
                    </Pill>
                  </div>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-stone-700 sm:grid-cols-2">
                    <div><dt className="inline text-stone-500">Last heartbeat: </dt><dd className="inline">{formatWhen(a.last_heartbeat_at)}</dd></div>
                    <div><dt className="inline text-stone-500">Last successful poll: </dt><dd className="inline">{formatWhen(a.last_successful_poll_at)}</dd></div>
                    <div><dt className="inline text-stone-500">Assigned printers: </dt><dd className="inline">{printerCount(a.id)}</dd></div>
                    <div><dt className="inline text-stone-500">App version: </dt><dd className="inline">{a.agent_version || a.app_version || "—"}</dd></div>
                    <div><dt className="inline text-stone-500">Supported providers: </dt><dd className="inline">{(a.supported_providers || []).join(", ") || ((a.platform || "windows") === "android" ? "Network ESC/POS" : "Windows spooler")}</dd></div>
                    <div><dt className="inline text-stone-500">Printer profiles: </dt><dd className="inline">{(a.supported_profiles || []).join(", ") || "—"}</dd></div>
                    {a.last_error_code && <div className="sm:col-span-2"><dt className="inline text-stone-500">Last error: </dt><dd className="inline">{a.last_error_code}</dd></div>}
                    {a.device_model && <div className="sm:col-span-2"><dt className="inline text-stone-500">Device: </dt><dd className="inline">{a.device_model} {a.android_version ? `(Android ${a.android_version})` : ""}</dd></div>}
                  </dl>
                  {isAdmin && !revoked && (
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={btnSecondary} onClick={() => setConfirm({ kind: "rotate", agent: a })} data-testid={`agent-rotate-${a.id}`}>Rotate token</button>
                      <button type="button" className={btnDanger} onClick={() => setConfirm({ kind: "revoke", agent: a })} data-testid={`agent-revoke-${a.id}`}>Revoke</button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {isAdmin && (
        <SectionCard title="Issue a new agent token" description="Create a token for a new tablet or PC. The token is shown once — copy it straight into the Print Agent app.">
          <form onSubmit={issue} className="grid grid-cols-1 gap-3 md:grid-cols-3" noValidate>
            <Field label="Agent name" required error={formError} hint="Where the agent runs, e.g. Kitchen tablet.">
              <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} data-testid="new-agent-name" maxLength={120} />
            </Field>
            <Field label="Platform">
              <select className={inputCls} value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="new-agent-platform">
                <option value="android">Android tablet (network ESC/POS printers)</option>
                <option value="windows">Windows PC (spooler)</option>
              </select>
            </Field>
            <div className="flex items-end">
              <button type="submit" className={btnPrimary} disabled={busy} data-testid="issue-agent-btn">{busy ? "Issuing…" : "Issue token"}</button>
            </div>
          </form>
        </SectionCard>
      )}

      <Modal open={Boolean(revealed)} onClose={closeReveal} testId="reveal-token-modal"
        title={revealed?.rotated ? "New token — copy it now" : "Agent token — copy it now"}
        description="This token is shown only once and cannot be viewed again. If you lose it, rotate the token to get a new one.">
        {revealed && (
          <div className="space-y-3">
            <Notice tone="warning">Paste it into the Rudras Print Agent app on <strong>{revealed.agent?.name}</strong>. The previous token {revealed.rotated ? "stops working immediately." : "does not exist yet."}</Notice>
            <label htmlFor="token-plaintext" className="block text-sm font-medium">Token</label>
            <textarea id="token-plaintext" readOnly value={revealed.token} rows={3} className="w-full rounded-md border border-stone-300 p-2 font-mono text-xs" data-testid="token-plaintext" onFocus={(e) => e.target.select()} />
            <div className="flex flex-wrap gap-2">
              <button type="button" className={btnPrimary} onClick={copyToken} data-testid="token-copy">{copied ? "Copied" : "Copy token"}</button>
              <button type="button" className={btnSecondary} onClick={closeReveal} data-testid="token-done">I have saved it</button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.kind === "revoke" ? `Revoke ${confirm?.agent?.name}?` : `Rotate the token for ${confirm?.agent?.name}?`}
        message={confirm?.kind === "revoke"
          ? "Revoking stops this agent from polling for print jobs on its next request. Queued jobs stay queued until another agent claims them. This cannot be undone; issue a new token to bring the device back."
          : "Rotating issues a new token and immediately invalidates the current one. The agent on that device stops printing until the new token is pasted in."}
        confirmLabel={confirm?.kind === "revoke" ? "Revoke token" : "Rotate token"}
        tone={confirm?.kind === "revoke" ? "danger" : "warning"}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirmed}
        testIdPrefix="agent-confirm"
      />
    </div>
  );
}
